<template>
  <v-card>
    <v-card-title class="ppt-title">
      <span>{{ $t('companies.periodic_payment_templates') }}</span>
      <v-spacer></v-spacer>
      <v-select
        v-model="selectedLang"
        :items="availableLanguages"
        :label="$t('companies.template_lang')"
        dense
        outlined
        hide-details
        class="mr-2 ppt-lang"
      ></v-select>
      <v-btn
        color="primary"
        class="ppt-create"
        @click="createDefaultTemplates"
        :loading="creatingDefaults"
      >
        <v-icon left>mdi-auto-fix</v-icon>
        {{ $t('periodic_templates.create_defaults') }}
      </v-btn>
    </v-card-title>
    
    <v-card-text>
      <v-alert v-if="templates.length === 0" type="info" text>
        {{ $t('periodic_templates.no_templates') }}
      </v-alert>

      <v-expansion-panels v-else multiple>
        <v-expansion-panel v-for="template in templates" :key="template.id">
          <v-expansion-panel-header>
            <v-row align="center" no-gutters>
              <v-col cols="12" sm="5">
                <v-chip :color="getTemplateColor(template.template_type)" dark small class="ppt-type-chip">
                  {{ $t(`periodic_templates.${template.template_type}`) }}
                </v-chip>
              </v-col>
              <v-col cols="5" sm="3" class="text-center">
                <v-chip small :color="template.enabled ? 'success' : 'grey'">
                  {{ template.enabled ? $t('custom.enabled') : $t('custom.disabled') }}
                </v-chip>
              </v-col>
              <v-col cols="4" sm="2" class="text-center">
                <v-chip small>{{ template.lang || $t('companies.default_lang') }}</v-chip>
              </v-col>
              <v-col cols="3" sm="2" class="text-right">
                <v-btn
                  icon
                  small
                  color="error"
                  @click.stop="deleteTemplate(template)"
                  :title="$t('custom.delete')"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          
          <v-expansion-panel-content>
            <v-form>
              <v-row>
                <v-col cols="12" md="6">
                  <v-switch 
                    v-model="template.enabled" 
                    :label="$t('custom.enabled')"
                    @change="updateTemplate(template)"
                  ></v-switch>
                </v-col>
                
                <v-col cols="12" md="6">
                  <v-select
                    v-model="template.lang"
                    :items="availableLanguages"
                    :label="$t('companies.template_lang')"
                    outlined
                    dense
                  ></v-select>
                </v-col>
                
                <v-col cols="12">
                  <v-text-field
                    v-model="template.subject"
                    :label="$t('periodic_templates.subject')"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
                
                <v-col cols="12">
                  <v-textarea
                    v-model="template.content"
                    :label="$t('periodic_templates.email_content')"
                    outlined
                    rows="6"
                    auto-grow
                  ></v-textarea>
                </v-col>
                
                <v-col cols="12">
                  <v-textarea
                    v-model="template.whatsapp_content"
                    :label="$t('periodic_templates.whatsapp_content')"
                    outlined
                    rows="4"
                    auto-grow
                  ></v-textarea>
                </v-col>
                
                <v-col cols="12">
                  <v-alert type="info" dense text>
                    <div class="text-caption">
                      <strong>{{ $t('periodic_templates.available_variables') }}:</strong>
                      <v-chip x-small class="mx-1" v-text="'{{client_name}}'"></v-chip>
                      <v-chip x-small class="mx-1" v-text="'{{booking_id}}'"></v-chip>
                      <v-chip x-small class="mx-1" v-text="'{{amount}}'"></v-chip>
                      <v-chip x-small class="mx-1" v-text="'{{currency}}'"></v-chip>
                      <v-chip x-small class="mx-1" v-text="'{{balance}}'"></v-chip>
                      <v-chip x-small class="mx-1" v-text="'{{new_balance}}'"></v-chip>
                      <v-chip x-small class="mx-1" v-text="'{{company_name}}'"></v-chip>
                      <v-chip x-small class="mx-1" v-text="'{{end_date}}'"></v-chip>
                      <v-chip x-small class="mx-1" v-text="'{{days_left}}'"></v-chip>
                    </div>
                  </v-alert>
                </v-col>
                
                <v-col cols="12" class="text-right">
                  <v-btn color="success" @click="updateTemplate(template)" :loading="template.saving">
                    <v-icon left>mdi-content-save</v-icon>
                    {{ $t('custom.save') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'PeriodicPaymentTemplates',
  
  data() {
    return {
      templates: [],
      creatingDefaults: false,
      selectedLang: this.$i18n.locale || 'ru',
      availableLanguages: [
        { text: this.$t("companies.default_lang"), value: null },
        { text: "English", value: "en" },
        { text: "Русский", value: "ru" },
        { text: "Português (Portugal)", value: "pt" },
        { text: "Português (Brasil)", value: "pt-BR" },
        { text: "Српски", value: "sr" },
        { text: "Thai", value: "th" },
        { text: "Ελληνικά", value: "el" },
        { text: "Hrvatski", value: "hr" },
        { text: "Turkish", value: "tr" },
        { text: "French", value: "fr" },
        { text: "German", value: "de" },
        { text: "Spanish", value: "es" },
        { text: "Italian", value: "it" },
        { text: "Indonesian", value: "id" },
        { text: "Polski", value: "pl" },
        { text: "Română", value: "ro" },
      ],
    };
  },
  
  mounted() {
    this.loadTemplates();
  },
  
  methods: {
    loadTemplates() {
      this.axios.get('/api/v1/periodic_payment_templates', {
        headers: {
          Authorization: this.$store.getters.getAuthToken
        }
      })
        .then((response) => {
          this.templates = response.data.map(t => ({ ...t, saving: false }));
        })
        .catch((error) => {
          this.setError(error, this.$t('errors.error'));
        });
    },
    
    createDefaultTemplates() {
      // Проверяем существуют ли уже шаблоны для выбранного языка
      if (this.hasTemplatesForLang(this.selectedLang)) {
        const langName = this.getLangName(this.selectedLang);
        this.$notify(
          this.$t('periodic_templates.templates_already_exist', { lang: langName }),
          '',
          'warning',
          5000
        );
        return;
      }
      
      this.creatingDefaults = true;
      this.axios.post('/api/v1/periodic_payment_templates/create_defaults', 
        { lang: this.selectedLang },
        {
          headers: {
            Authorization: this.$store.getters.getAuthToken
          }
        }
      )
        .then((response) => {
          // Добавляем новые шаблоны к существующим, а не заменяем
          const newTemplates = response.data.map(t => ({ ...t, saving: false }));
          this.templates = [...this.templates, ...newTemplates];
          this.$notify(this.$t('periodic_templates.defaults_created'), '', 'success', 3000);
        })
        .catch((error) => {
          this.setError(error, this.$t('errors.error'));
        })
        .finally(() => {
          this.creatingDefaults = false;
        });
    },
    
    updateTemplate(template) {
      template.saving = true;
      this.axios.patch(`/api/v1/periodic_payment_templates/${template.id}`, 
        {
          periodic_payment_template: {
            subject: template.subject,
            content: template.content,
            whatsapp_content: template.whatsapp_content,
            enabled: template.enabled,
            lang: template.lang,
          }
        },
        {
          headers: {
            Authorization: this.$store.getters.getAuthToken
          }
        }
      )
        .then((response) => {
          this.$notify(this.$t('custom.saved'), '', 'success', 3000);
        })
        .catch((error) => {
          this.setError(error, this.$t('errors.error'));
        })
        .finally(() => {
          template.saving = false;
        });
    },
    
    async deleteTemplate(template) {
      const templateName = this.$t(`periodic_templates.${template.template_type}`);
      const lang = template.lang || this.$t('companies.default_lang');
      
      const confirmed = await this.$confirm({
        title: this.$t("custom.you_sure_title"),
        text: "",
        icon: "mdi-delete-outline",
        iconColor: "error",
        iconSize: 72,
        iconPosition: "top",
        confirmButtonText: this.$t("custom.you_sure_yes"),
        confirmButtonColor: "error",
        cancelButtonText: this.$t("custom.you_sure_no"),
        cancelButtonColor: "grey darken-1",
        useFilledButtons: true,
        maxWidth: 450,
      });
      
      if (!confirmed) return;
      
      this.axios.delete(`/api/v1/periodic_payment_templates/${template.id}`, {
        headers: {
          Authorization: this.$store.getters.getAuthToken
        }
      })
        .then((response) => {
          this.templates = this.templates.filter(t => t.id !== template.id);
          this.$notify(this.$t('custom.deleted'), '', 'success', 3000);
        })
        .catch((error) => {
          this.setError(error, this.$t('errors.error'));
        });
    },
    
    getTemplateColor(type) {
      const colors = {
        payment_success: 'success',
        payment_failed: 'error',
        balance_warning: 'warning',
        balance_negative: 'deep-orange',
        balance_critical: 'red darken-4',
      };
      return colors[type] || 'primary';
    },
    
    hasTemplatesForLang(lang) {
      return this.templates.some(t => t.lang === lang || (lang === null && t.lang === null));
    },
    
    getLangName(lang) {
      const langItem = this.availableLanguages.find(l => l.value === lang);
      return langItem ? langItem.text : lang;
    },
  },
};
</script>

<style scoped>
/* v-card__title в Vuetify 2 ломает слова посередине (word-break: break-all) */
.ppt-title {
  word-break: normal;
}
.ppt-lang {
  max-width: 200px;
}
/* xs: селект языка и кнопка шаблонов не помещаются в строку заголовка — в столбик,
   чипу типа шаблона даём многострочность вместо обрезания */
@media (max-width: 599px) {
  .ppt-lang {
    max-width: 100%;
    flex-basis: 100%;
    margin-right: 0 !important;
    margin-top: 8px;
  }
  .ppt-create {
    width: 100%;
    margin-top: 8px;
    height: auto !important;
    min-height: 36px;
    padding-top: 6px;
    padding-bottom: 6px;
  }
  .ppt-create ::v-deep .v-btn__content {
    white-space: normal;
    flex-shrink: 1;
  }
  .ppt-type-chip {
    height: auto;
    min-height: 24px;
    max-width: 100%;
    white-space: normal;
    margin-bottom: 4px;
  }
  .ppt-type-chip ::v-deep .v-chip__content {
    white-space: normal;
    overflow-wrap: anywhere;
    padding-top: 2px;
    padding-bottom: 2px;
  }
}
</style>

